import type { LucideIcon } from "lucide-react";
import { CalendarClock, Clapperboard, Home, Megaphone, Sparkles, Subtitles, Target } from "lucide-react";
import type { CardSlug } from "./rules/deepLinks";

/** null = not known yet (the request is loading or failed); an unknown answer never blocks a card. */
export interface EligibilityContext {
    /** Property campaigns (the campaign list's rows), not listings: a listing without a campaign has no row. */
    listingCount: number | null;
    activeListingCount: number | null;
    /** Whether any listing is on the account, campaign or not; asked only when there is no property campaign. */
    hasListings: boolean | "checking" | null;
    captionMinutesLeft: number | null;
    nicheEligible: boolean | null;
}
export type Eligibility =
    | { ok: true }
    | { ok: false; checking: true }
    | { ok: false; checking?: false; note: string; link?: { label: string; to: string } };

export type SectionId = "listing" | "brand" | "video";
export interface CardSection { id: SectionId; title: string; hint: string }

export interface CardDef {
    slug: CardSlug;
    section: SectionId;
    name: string;
    sentence: string;
    icon: LucideIcon;
    eligibility: (ctx: EligibilityContext) => Eligibility;
}

/** Display order. Captioned Video can be about a listing or the brand, so the recorded cards get their own section. */
export const SECTIONS: readonly CardSection[] = [
    { id: "listing", title: "Promote a listing", hint: "Posts about one of your listings. Pick the listing first." },
    { id: "brand", title: "Build your brand", hint: "Posts about you and your business. No listing needed." },
    { id: "video", title: "Record a video", hint: "Talk to your audience on video. Marabot adds captions and writes the post." },
];

// The minutes left are not shown on the card (the step's usage bar has them); only running out blocks it.
const captionMinutes = (ctx: EligibilityContext): Eligibility =>
    ctx.captionMinutesLeft !== null && ctx.captionMinutesLeft <= 0 ? { ok: false, note: "Captioning minutes used up for this month" } : { ok: true };

// No property campaign: with a listing on the account the missing piece is the campaign, not the listing.
const needsListing = (ctx: EligibilityContext): Eligibility => {
    if (ctx.listingCount === null || ctx.listingCount > 0) return { ok: true };
    // A note now would flip once the answer lands, so the card shows a skeleton until then.
    if (ctx.hasListings === "checking") return { ok: false, checking: true };
    const note = ctx.hasListings === true ? "Create a campaign on an active listing first" : "Add a listing and create a campaign first";
    return { ok: false, note, link: { label: "Listings", to: "/listings" } };
};

// A paused campaign takes a Listing post but not an open house or video showcase post.
const needsActiveListing = (ctx: EligibilityContext): Eligibility => {
    const base = needsListing(ctx);
    if (!base.ok || ctx.activeListingCount === null || ctx.activeListingCount > 0) return base;
    return { ok: false, note: "Your listing campaigns are paused", link: { label: "Listings", to: "/listings" } };
};

export const CARDS: CardDef[] = [
    { slug: "listing", section: "listing", name: "Listing Post", icon: Home, sentence: "Pick a listing. Marabot writes a post about it for each channel, or you write it.", eligibility: needsListing },
    { slug: "open-house", section: "listing", name: "Open House", icon: CalendarClock, sentence: "Date, time and photos. Marabot writes the event post for each channel.", eligibility: needsActiveListing },
    { slug: "video-showcase", section: "listing", name: "Video Showcase", icon: Clapperboard, sentence: "One listing video, posted with copy written for a walkthrough, school district, feature or neighborhood.", eligibility: needsActiveListing },
    { slug: "my-brand", section: "brand", name: "My Brand Post", icon: Megaphone, sentence: "Your words and your media, posted word for word on every channel you use.", eligibility: () => ({ ok: true }) },
    { slug: "lead-gen", section: "brand", name: "Lead Generation", icon: Sparkles, sentence: "Pick a lead type and a one-line hook. It becomes a graphic post on every channel.", eligibility: () => ({ ok: true }) },
    { slug: "captioned", section: "video", name: "Captioned Video", icon: Subtitles, sentence: "Record or upload a video. Captions are added and the post text is written from what you said.",
      eligibility: captionMinutes },
    { slug: "niche", section: "video", name: "My Niche Video", icon: Target, sentence: "A topic and script from your niche, recorded in the app, captioned and posted.",
      eligibility: (ctx) => (ctx.nicheEligible === false ? { ok: false, note: "AI Pro", link: { label: "Learn more", to: "/my-niche" } } : { ok: true }) },
];

export const cardsInSection = (id: SectionId): CardDef[] => CARDS.filter((c) => c.section === id);

export function cardEligibility(def: CardDef, ctx: EligibilityContext): Eligibility { return def.eligibility(ctx); }
